import {
    DecimalRange,
    ExtendedApartmentBuilding,
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
    mergeConstructionNumberSpecification,
    mergeDecimalRange,
    mergePropertySpecification,
    PropertySpecifications,
} from '@bpd-library/utilities';

import { useTranslate } from '../hooks';

interface FormattedSpecification {
    value: string;
    suffix?: Text | string;
}

type FormatSpecificationFunction = (
    extendedObject:
        | ExtendedApartmentBuilding
        | ExtendedPropertyType
        | ExtendedConstructionNumber
        | ExtendedProjectPhase,
) => FormattedSpecification | undefined;

const rangeAsString = (range: DecimalRange) => {
    let str;

    if (range.from) str = `${range.from}`;
    if (range.to) str = str ? `${str} - ${range.to}` : `${range.to}`;

    return str || '';
};

const formatGenericSpecification = ({
    specification,
    extendedObject,
    suffix,
}: {
    specification: keyof PropertySpecifications;
    extendedObject:
        | ExtendedApartmentBuilding
        | ExtendedPropertyType
        | ExtendedConstructionNumber
        | ExtendedProjectPhase;
    suffix?: string | Text;
}) => {
    let range: DecimalRange | undefined;

    switch (extendedObject.objectType) {
        case 'constructionNumber':
        case 'propertyType':
            range = extendedObject.specifications
                ? extendedObject.specifications[specification]
                : undefined;
            break;
        case 'apartmentBuilding':
        case 'projectPhase':
            const constructionNumbersRange = mergeConstructionNumberSpecification(
                extendedObject.constructionNumbers || [],
                specification,
            );
            const propertyTypesRange = mergePropertySpecification(
                { propertyTypes: extendedObject.propertyTypes || [] },
                specification,
            );

            range = mergeDecimalRange(constructionNumbersRange, propertyTypesRange);
            break;
    }

    return range && (range.from || range.to)
        ? {
              value: rangeAsString(range),
              suffix,
          }
        : undefined;
};

/**
 * @description Will return the formatted rooms specification of an ExtendedConstructionNumber, ExtendedPropertyType or ExtendedApartmentBuilding
 * @param {*} extendedObject
 */
const formatRooms: FormatSpecificationFunction = (extendedObject) =>
    formatGenericSpecification({
        specification: 'rooms',
        extendedObject,
        suffix: useTranslate('Rooms'),
    });

/**
 * @description Will return the formatted bedrooms specification of an ExtendedConstructionNumber, ExtendedPropertyType or ExtendedApartmentBuilding
 * @param {*} extendedObject
 */
const formatBedrooms: FormatSpecificationFunction = (extendedObject) =>
    formatGenericSpecification({
        specification: 'bedrooms',
        extendedObject,
        suffix: useTranslate('Rooms'),
    });

/**
 * @description Will return the formatted living area specification of an ExtendedConstructionNumber, ExtendedPropertyType or ExtendedApartmentBuilding
 * @param {*} extendedObject
 */
const formatLivingArea: FormatSpecificationFunction = (extendedObject) =>
    formatGenericSpecification({ specification: 'livingArea', extendedObject, suffix: 'm²' });

/**
 * @description Will return the formatted plot area specification of an ExtendedConstructionNumber, ExtendedPropertyType or ExtendedApartmentBuilding
 * @param {*} extendedObject
 */
const formatPlotArea: FormatSpecificationFunction = (extendedObject) =>
    formatGenericSpecification({ specification: 'plotArea', extendedObject, suffix: 'm²' });

/**
 * @description Will return the formatted living room area specification of an ExtendedConstructionNumber, ExtendedPropertyType or ExtendedApartmentBuilding
 * @param {*} extendedObject
 */
const formatLivingRoomArea: FormatSpecificationFunction = (extendedObject) =>
    formatGenericSpecification({ specification: 'livingRoomArea', extendedObject, suffix: 'm²' });

/**
 * @description Will return the formatted volume specification of an ExtendedConstructionNumber, ExtendedPropertyType or ExtendedApartmentBuilding
 * @param {*} extendedObject
 */
const formatVolume: FormatSpecificationFunction = (extendedObject) =>
    formatGenericSpecification({ specification: 'volume', extendedObject, suffix: 'm³' });

export {
    FormattedSpecification,
    formatRooms,
    formatBedrooms,
    formatLivingArea,
    formatPlotArea,
    formatLivingRoomArea,
    formatVolume,
};
